import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { api } from "../api";
import { ShowToast } from "../../components/toast";

export interface MlosRestriction {
  id: string;
  userId: string;
  date: string;
  minLos: number;
  source: "auto" | "manual" | "pms_imported" | "shadow";
  pmsSyncStatus?: "synced" | "pending" | "failed" | "not_applicable";
  roomTypeId: string | null;
  roomTypeName: string | null;
  autoRemoveOnInventoryMismatch?: boolean;
  evaluationContext?: Record<string, unknown>;
  removedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface MlosConfig {
  enableMlos?: boolean;
  /** Shows the Length of Stay tab (incl. the enable wizard) without enabling
   *  the engine; enableMlos remains the master switch. Set by CS/admin. */
  showMlosTab?: boolean;
  hasMewsImportedRestrictions?: boolean;
  mlos2OccupancyThreshold?: number;
  mlos3OccupancyThreshold?: number;
  removalOccupancyThreshold?: number;
  paceThreshold?: number;
  minDaysOut?: number;
  nearArrivalDays?: number;
  inventoryBufferPct?: number;
  enableRoomTypeLevel?: boolean;
  excludedRoomTypeIds?: string[];
}

export interface MlosRoomType {
  id: string;
  pmsId: string;
  name: string;
  totalRooms: number;
  isActive: boolean;
  isExcludedFromMlos: boolean;
}

export interface MlosRoomTypeDetail {
  roomTypeId: string;
  roomTypeName: string;
  totalRooms: number;
  restriction: {
    id: string;
    minLos: number;
    source: "auto" | "manual" | "pms_imported";
    pmsSyncStatus: "synced" | "pending" | "failed" | "not_applicable";
  } | null;
  availability: number[];
  status: "active" | "lifted" | "excluded" | "manual";
  liftReason?: string;
}

export interface MlosDateSummary {
  totalRoomTypes: number;
  activeCount: number;
  liftedCount: number;
  minLosRange: [number, number];
}

export interface MlosRestrictionByDate {
  date: string;
  roomTypes: MlosRoomTypeDetail[];
  summary: MlosDateSummary;
}

export interface MlosDecisionLogEntry {
  id: string;
  userId: string;
  evaluationEventId: string;
  date: string;
  decision: string;
  minLos: number | null;
  trigger: string;
  reason: string;
  roomTypeId: string | null;
  roomTypeName: string | null;
  context: Record<string, unknown>;
  createdAt: string;
}

const MLOS_KEYS = {
  config: (userId: string) => ["mlosConfig", userId],
  allRestrictions: (userId: string) => ["mlosRestrictions", userId],
  restrictions: (userId: string, startDate?: string, endDate?: string) => [
    "mlosRestrictions",
    userId,
    startDate,
    endDate,
  ],
  restrictionsByDate: (userId: string, date: string) => [
    "mlosRestrictionsByDate",
    userId,
    date,
  ],
  roomTypes: (userId: string) => ["mlosRoomTypes", userId],
  preview: (userId: string) => ["mlosPreview", userId],
  importPreview: (userId: string) => ["mlosImportPreview", userId],
  decisionLog: (userId: string, date?: string, roomTypeId?: string) => [
    "mlosDecisionLog",
    userId,
    date,
    roomTypeId,
  ],
};

export function useMlosConfig(userId: string) {
  return useQuery<MlosConfig>({
    queryKey: MLOS_KEYS.config(userId),
    queryFn: () => api.mlos.getConfig(userId) as Promise<MlosConfig>,
    enabled: !!userId,
    staleTime: 30 * 1000,
  });
}

export function useUpdateMlosConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, config }: { userId: string; config: any }) =>
      api.mlos.updateConfig(userId, config),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: MLOS_KEYS.config(variables.userId),
      });
      // Skip generic toast when enableMlos is being toggled — caller shows its own
      if (variables.config.enableMlos === undefined) {
        ShowToast({ message: "MLOS settings updated", type: "success" });
      }
    },
    onError: () => {
      ShowToast({ message: "Failed to update MLOS settings", type: "error" });
    },
  });
}

export function useMlosRestrictions(
  userId: string,
  startDate?: string,
  endDate?: string,
  options?: { enabled?: boolean },
) {
  return useQuery<MlosRestriction[]>({
    queryKey: MLOS_KEYS.restrictions(userId, startDate, endDate),
    queryFn: () => api.mlos.getRestrictions(userId, { startDate, endDate }) as Promise<MlosRestriction[]>,
    enabled: !!userId && (options?.enabled ?? true),
    staleTime: 30 * 1000,
  });
}

export function useSetManualMlos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      date,
      minLos,
      autoRemoveOnInventoryMismatch,
    }: {
      userId: string;
      date: string;
      minLos: number;
      autoRemoveOnInventoryMismatch?: boolean;
    }) => api.mlos.setManualRestriction(userId, { date, minLos, autoRemoveOnInventoryMismatch }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: MLOS_KEYS.allRestrictions(variables.userId),
      });
      ShowToast({
        message: `Min Stay ${variables.minLos} set for ${moment(variables.date).format("MMM D, YYYY")}`,
        type: "success",
      });
    },
    onError: () => {
      ShowToast({
        message: "Failed to set MLOS restriction",
        type: "error",
      });
    },
  });
}

export function useRemoveManualMlos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, date }: { userId: string; date: string }) =>
      api.mlos.removeRestriction(userId, date),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: MLOS_KEYS.allRestrictions(variables.userId),
      });
      ShowToast({ message: `Min Stay removed for ${moment(variables.date).format("MMM D, YYYY")}`, type: "success" });
    },
    onError: () => {
      ShowToast({
        message: "Failed to remove MLOS restriction",
        type: "error",
      });
    },
  });
}

export function useTriggerMlosEvaluation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      dates,
    }: {
      userId: string;
      dates?: string[];
    }) => api.mlos.triggerEvaluation(userId, { dates }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: MLOS_KEYS.allRestrictions(variables.userId),
      });
      ShowToast({ message: "MLOS evaluation complete", type: "success" });
    },
    onError: () => {
      ShowToast({ message: "MLOS evaluation failed", type: "error" });
    },
  });
}

export function useMlosPreview(userId: string) {
  return useQuery({
    queryKey: MLOS_KEYS.preview(userId),
    queryFn: () => api.mlos.getPreview(userId),
    enabled: false, // Only fetch when explicitly requested
  });
}

export function useMlosImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.mlos.importRestrictions(userId),
    onSuccess: (data, userId) => {
      queryClient.invalidateQueries({
        queryKey: MLOS_KEYS.config(userId),
      });
      queryClient.invalidateQueries({
        queryKey: MLOS_KEYS.allRestrictions(userId),
      });
      if (data?.imported > 0) {
        ShowToast({ message: "PMS restrictions imported", type: "success" });
      }
    },
    onError: () => {
      ShowToast({
        message: "Failed to import PMS restrictions",
        type: "error",
      });
    },
  });
}

export function useMlosImportPreview(userId: string) {
  return useQuery({
    queryKey: MLOS_KEYS.importPreview(userId),
    queryFn: () => api.mlos.getImportPreview(userId),
    enabled: !!userId,
  });
}

export function useMlosDecisionLog(userId: string, date?: string, roomTypeId?: string) {
  return useQuery<MlosDecisionLogEntry[]>({
    queryKey: MLOS_KEYS.decisionLog(userId, date, roomTypeId),
    queryFn: () => api.mlos.getDecisionLog(userId, { date, limit: 50, roomTypeId }) as Promise<MlosDecisionLogEntry[]>,
    enabled: !!userId,
    staleTime: 60 * 1000,
  });
}

export function useGenerateMlosPreview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.mlos.generatePreview(userId),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({
        queryKey: MLOS_KEYS.allRestrictions(userId),
      });
      ShowToast({ message: "MLOS preview generated", type: "success" });
    },
    onError: () => {
      ShowToast({ message: "Failed to generate MLOS preview", type: "error" });
    },
  });
}

export function useActivateMlos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.mlos.activate(userId),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({
        queryKey: MLOS_KEYS.config(userId),
      });
      queryClient.invalidateQueries({
        queryKey: MLOS_KEYS.allRestrictions(userId),
      });
      ShowToast({ message: "MLOS automation activated", type: "success" });
    },
    onError: () => {
      ShowToast({ message: "Failed to activate MLOS", type: "error" });
    },
  });
}

// ─── Room-Type-Level Hooks (MLOS 2.0) ────────────────────────────────

export function useMlosRoomTypes(
  userId: string,
  options?: { enabled?: boolean },
) {
  return useQuery<MlosRoomType[]>({
    queryKey: MLOS_KEYS.roomTypes(userId),
    queryFn: () => api.mlos.getRoomTypes(userId) as Promise<MlosRoomType[]>,
    enabled: !!userId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  });
}

export function useMlosRestrictionsByDate(userId: string, date?: string) {
  return useQuery<MlosRestrictionByDate>({
    queryKey: MLOS_KEYS.restrictionsByDate(userId, date || ""),
    queryFn: () => api.mlos.getRestrictionsByDate(userId, date!) as Promise<MlosRestrictionByDate>,
    enabled: !!userId && !!date,
    staleTime: 30 * 1000,
  });
}

export function useSetManualMlosForRoomType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      date,
      minLos,
      roomTypeId,
    }: {
      userId: string;
      date: string;
      minLos: number;
      roomTypeId: string;
    }) =>
      api.mlos.setManualRestriction(userId, {
        date,
        minLos,
        roomTypeId,
        autoRemoveOnInventoryMismatch: false,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: MLOS_KEYS.allRestrictions(variables.userId),
      });
      queryClient.invalidateQueries({
        queryKey: MLOS_KEYS.restrictionsByDate(variables.userId, variables.date),
      });
      ShowToast({
        message: `Min Stay ${variables.minLos} applied for ${moment(variables.date).format("MMM D, YYYY")}`,
        type: "success",
      });
    },
    onError: () => {
      ShowToast({
        message: "Failed to apply MLOS restriction",
        type: "error",
      });
    },
  });
}

export function useRemoveMlosForRoomType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      date,
      roomTypeId,
    }: {
      userId: string;
      date: string;
      roomTypeId: string;
    }) => api.mlos.removeRestriction(userId, date, roomTypeId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: MLOS_KEYS.allRestrictions(variables.userId),
      });
      queryClient.invalidateQueries({
        queryKey: MLOS_KEYS.restrictionsByDate(variables.userId, variables.date),
      });
      ShowToast({
        message: `Min Stay removed for ${moment(variables.date).format("MMM D, YYYY")}`,
        type: "success",
      });
    },
    onError: () => {
      ShowToast({
        message: "Failed to remove MLOS restriction",
        type: "error",
      });
    },
  });
}
